import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useUpdateObject } from '@/features/objects/hooks';
import { apiGet } from '@/lib/api/client';
import { TaskEditModal } from '@/modules/tasks/TaskEditModal';
import type { TaskObject } from '@/modules/tasks/taskEditModal.types';
import type { TaskCardData } from './types';

const SEARCH_THRESHOLD = 5;

export function TaskResultCard({
  data,
  onOpenBoard,
}: {
  data: TaskCardData;
  onOpenBoard?: (taskId: string, boardId: string) => void;
}) {
  const tasks = data.tasks ?? [];
  const update = useUpdateObject('task');
  const [editingTask, setEditingTask] = useState<TaskObject | null>(null);
  const [editingEtag, setEditingEtag] = useState<string>('');
  const [search, setSearch] = useState('');

  if (!tasks.length) return null;

  const filtered = search.trim()
    ? tasks.filter(t => t.title.toLowerCase().includes(search.toLowerCase()))
    : tasks;

  async function handleClickTask(id: string) {
    try {
      const { data: raw, etag } = await apiGet<any>(`/tasks/${id}`);
      const task: TaskObject = {
        id,
        type: 'task',
        title: raw?.title ?? '',
        status: raw?.status,
        priority: raw?.priority,
        dueAt: raw?.dueAt,
        // Carry the description so the edit modal shows it (same read-back drop
        // as the Kanban card's toTaskObject) — it's flattened onto the response.
        description: raw?.description,
        label: raw?.label,
        labelColor: raw?.labelColor,
        notes: raw?.notes,
        assignedTo: raw?.assignedTo,
        githubUrl: raw?.githubUrl,
        createdAt: raw?.createdAt,
        authorId: raw?.authorId,
        authorName: raw?.authorName,
      };
      setEditingEtag(etag ?? raw?._etag ?? String(raw?.version ?? ''));
      setEditingTask(task);
    } catch {
      // silently ignore — task may have been deleted
    }
  }

  async function handleSave(patch: Partial<TaskObject>) {
    if (!editingTask) return;
    await update.mutateAsync({ id: editingTask.id, etag: editingEtag, patch: patch as any });
    setEditingTask(null);
  }

  return (
    <>
      <div className="rc-card rc-tasks">
        <div className="rc-meta">
          <span className="rc-meta-kind">✅ TASKS</span>
          <span className="rc-meta-sep">·</span>
          <span className="rc-meta-match">{tasks.length}</span>
        </div>

        {tasks.length > SEARCH_THRESHOLD && (
          <div className="rc-task-search-wrap">
            <input
              className="rc-task-search"
              type="text"
              placeholder="Filter tasks…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        )}

        <ul className="rc-task-list">
          {filtered.map((t) => (
            <li key={t.id} className="rc-task-row rc-task-row--clickable">
              <button
                className="rc-task-main"
                onClick={() => void handleClickTask(t.id)}
                type="button"
              >
                <span className={`rc-task-title${t.completed ? ' rc-task-done' : ''}`}>{t.title}</span>
                {t.due && <span className="rc-task-due">{t.due}</span>}
              </button>
              {t.boardId && onOpenBoard && (
                <button
                  className="rc-task-board-btn"
                  onClick={(e) => { e.stopPropagation(); onOpenBoard(t.id, t.boardId!); }}
                  title={t.boardName ? `Open on board: ${t.boardName}` : 'Open on kanban board'}
                  type="button"
                >
                  <span style={{ fontSize: 12 }}>⊞</span>
                  {t.boardName && <span className="rc-task-board-name">{t.boardName}</span>}
                </button>
              )}
            </li>
          ))}
          {filtered.length === 0 && search && (
            <li className="rc-task-row" style={{ padding: '8px 6px', color: 'var(--color-text-quiet, #9aa094)', fontSize: 13 }}>
              No tasks match "{search}"
            </li>
          )}
        </ul>
      </div>

      {editingTask && createPortal(
        <TaskEditModal
          task={editingTask}
          onClose={() => setEditingTask(null)}
          onSave={(patch) => void handleSave(patch)}
        />,
        document.body,
      )}
    </>
  );
}
